import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Sun, Moon, ArrowRight, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function AMPMPage() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });

  const [am, setAm] = useState([]);
  const [pm, setPm] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setAm(settings.ampm_am || []);
      setPm(settings.ampm_pm || []);
    }
  }, [settings]);

  const updateAt = (list, setList, idx, value) => {
    setList(list.map((p, i) => (i === idx ? { ...p, name: value } : p)));
  };
  const addTo = (list, setList) =>
    setList([...list, { name: "", grade: "" }]);
  const removeAt = (list, setList, idx) =>
    setList(list.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const cleanAm = am.filter((p) => (p.name || "").trim());
      const cleanPm = pm.filter((p) => (p.name || "").trim());
      await api.patch("/settings", { ampm_am: cleanAm, ampm_pm: cleanPm });
      toast.success("AM/PM staff saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="ampm-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
            Tip Letters
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-[#0B2545]">
            AM &amp; PM Care Tip Letter
          </h1>
          <p className="text-[#13315C]/75 mt-2 max-w-2xl text-sm sm:text-base">
            One dedicated letter for the AM &amp; PM Care staff. Edit rates in{" "}
            <Link to="/admin" className="underline">Admin → Tip Amounts (AM/PM Care)</Link>.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            data-testid="ampm-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0B2545] text-[#FDF6EC] font-semibold hover:bg-[#13315C] disabled:opacity-70"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save staff"}
          </button>
          <Link
            to="/letter/ampm"
            data-testid="ampm-letter-link"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[#0B2545] font-semibold border border-[#0B2545] hover:bg-[#0B2545] hover:text-[#FDF6EC]"
          >
            Open printable letter <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StaffCard
          title="AM Care"
          icon={Sun}
          testidBase="ampm-am"
          staff={am}
          setStaff={setAm}
          onUpdate={(i, v) => updateAt(am, setAm, i, v)}
          onAdd={() => addTo(am, setAm)}
          onRemove={(i) => removeAt(am, setAm, i)}
        />
        <StaffCard
          title="PM Care"
          icon={Moon}
          testidBase="ampm-pm"
          staff={pm}
          setStaff={setPm}
          onUpdate={(i, v) => updateAt(pm, setPm, i, v)}
          onAdd={() => addTo(pm, setPm)}
          onRemove={(i) => removeAt(pm, setPm, i)}
        />
      </div>
    </div>
  );
}

function StaffCard({ title, icon: Icon, testidBase, staff, onUpdate, onAdd, onRemove }) {
  return (
    <section className="paper-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#0B2545] text-[#FDF6EC]">
            <Icon className="w-4 h-4" />
          </span>
          <h2 className="font-display text-2xl font-bold text-[#0B2545]">{title}</h2>
        </div>
        <span className="font-mono-camp text-[10px] tracking-widest text-[#13315C]/60 uppercase">
          {staff.length} {staff.length === 1 ? "person" : "people"}
        </span>
      </div>

      <div className="space-y-2">
        {staff.length === 0 && (
          <div className="italic text-sm text-[#13315C]/50">No staff yet — click &ldquo;Add&rdquo; below.</div>
        )}
        {staff.map((p, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`${testidBase}-row-${i}`}>
            <input
              value={p.name || ""}
              onChange={(e) => onUpdate(i, e.target.value)}
              placeholder="Counselor name"
              data-testid={`${testidBase}-name-${i}`}
              className="flex-1 px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
            />
            <button
              onClick={() => onRemove(i)}
              data-testid={`${testidBase}-remove-${i}`}
              className="p-2 rounded-md text-[#B54223] hover:bg-[#B54223]/10"
              title="Remove"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={onAdd}
        data-testid={`${testidBase}-add`}
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E9DDC8] text-[#0B2545] text-xs font-semibold hover:border-[#0B2545]"
      >
        <Plus className="w-3.5 h-3.5" /> Add {title.split(" ")[0]} counselor
      </button>
    </section>
  );
}
